import { Avatar, Button, DropdownMenu, Skeleton } from "@/components/ui";
import { PlayerAccount } from "@/interfaces";
import { extractInitials, fullNameFrom } from "@/utils";
import React from "react";
import { toast } from "sonner";
import { Settings, Key, Pencil } from "lucide-react";
import { UserChangePasswordForm } from "@/containers/UserChangePasswordForm";
import PlayerEditProfileDialog from "./PlayerEditProfileDialog";

interface Props {
  playerAccount?: PlayerAccount;
  fetching: boolean;
  onSuccess: () => void;
}

export function PlayerProfileHeader({ playerAccount, fetching, onSuccess }: Props) {
  const [isEditingProfile, setIsEditingProfile] = React.useState(false);
  const withFullName = Boolean(playerAccount?.firstName || playerAccount?.lastName);

  const handleCopyEmail = () => {
    if (playerAccount?.email) {
      navigator.clipboard
        .writeText(playerAccount.email)
        .then(() => toast.success("Email copied to clipboard!"))
        .catch(() => toast.error("Failed to copy email to clipboard."));
    }
  };

  return (
    <>
      <div className="flex items-center gap-4 w-full mb-2">
        <Skeleton loading={fetching} className="rounded-full shrink-0">
          <Avatar size="xl">
            <Avatar.Image src={playerAccount?.photoUrl} />
            <Avatar.Fallback className="text-xl">
              {extractInitials(playerAccount?.firstName, playerAccount?.lastName)}
            </Avatar.Fallback>
          </Avatar>
        </Skeleton>

        <div className="flex flex-col text-slate-800 flex-1 min-w-0">
          <div className="flex items-center">
            <Skeleton loading={fetching} className="rounded-md w-fit">
              <span className="font-semibold text-lg truncate">
                {playerAccount && withFullName
                  ? fullNameFrom(playerAccount)
                  : fetching
                  ? "Loading..."
                  : "No Name Provided"}
              </span>
              {!fetching && playerAccount && (
                <DropdownMenu>
                  <DropdownMenu.Trigger
                    render={props => (
                      <Button
                        {...props}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full text-slate-400 hover:text-slate-800"
                      >
                        <span className="sr-only">Open menu</span>
                        <Settings className="h-4 w-4" />
                      </Button>
                    )}
                  />
                  <DropdownMenu.Content>
                    <DropdownMenu.Item onClick={() => setIsEditingProfile(true)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      <span>Edit Profile</span>
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item
                      onClick={() => {
                        document.getElementById(`hidden-change-password-trigger-${playerAccount?.playerId}`)?.click();
                      }}
                    >
                      <Key className="mr-2 h-4 w-4" />
                      <span>Change Password</span>
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu>
              )}
            </Skeleton>
          </div>

          <div className="flex items-center gap-2 text-slate-800 mt-1">
            <Skeleton loading={fetching} className="rounded-md w-fit max-w-full">
              <Button
                onClick={handleCopyEmail}
                title="Copy to clipboard"
                className="p-0 m-0 h-fit rounded-none hover:underline text-left cursor-pointer transition-colors text-slate-700 hover:text-slate-600 truncate max-w-full font-normal"
                disabled={fetching || !playerAccount?.email}
                variant="link"
              >
                {playerAccount?.email || (fetching ? "player.email@placeholder.com" : "No Email Provided")}
              </Button>
            </Skeleton>
          </div>
        </div>
      </div>

      {isEditingProfile && playerAccount && (
        <PlayerEditProfileDialog
          playerAccount={playerAccount}
          open={isEditingProfile}
          onOpenChange={setIsEditingProfile}
          onSuccess={onSuccess}
        />
      )}

      {playerAccount && (
        <UserChangePasswordForm
          trigger={<button id={`hidden-change-password-trigger-${playerAccount.playerId}`} className="hidden" />}
        />
      )}
    </>
  );
}
